import {
  Button,
  FormErrors,
  InputContainer,
  InputDate,
  InputNumber,
  InputSelect,
  InputSwitch,
  Label,
  Option,
  useForm,
} from "../components/form";
import { Dollar, Info, Percent, Plus, Trash } from "../icons";
import { Alert } from "../components/alert";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ILoan } from "./Loan";
import { ILiability, LendersData } from "../liabilities";
import { format, parse } from "date-fns";
import { SectionLabel } from "../components/label";
import { AnimatePresence, motion } from "framer-motion";
import classNames from "classnames";
import { Frequency } from "../applicant";
import useStore from "../store";

type Values = Omit<ILoan, "_id" | "Security"> & {
  TotalTermYears?: number;
  InterestOnlyTermYears?: number;
  Liability?: Partial<Omit<ILiability, "_id">> & {
    InterestOnlyTermYears?: number | null;
    TotalTermYears?: number | null;
  };
};

const frequency: {
  value: Frequency;
  label: string;
}[] = [
  {
    value: "Yearly",
    label: "Yearly",
  },
  {
    value: "HalfYearly",
    label: "Half-yearly",
  },
  {
    value: "Quarterly",
    label: "Quarterly",
  },
  {
    value: "Monthly",
    label: "Monthly",
  },
  {
    value: "Fortnightly",
    label: "Fortnightly",
  },
  {
    value: "Weekly",
    label: "Weekly",
  },
];

const defaultValues = {
  Value: NaN,
  UsageType: null,
  PropertyType: null,
  PropertySubType: null,
  PropertySubSubType: null,
  SecurityType: "Proposed",
  Location: null,
  IsRBNZExempt: false,
  Lender: null,
  LoanType: "New",
  TopUpAmount: NaN,
  LendingAction: null,
  LoanAmount: NaN,
  TotalTermYears: NaN,
  TotalTermMonths: 12 * 30,
  InterestOnlyTermYears: NaN,
  InterestOnlyTermMonths: NaN,
  LiabilityType: "HomeLoan",
  Liability: {
    LiabilityType: "HomeLoan",
    RepaymentAmount: NaN,
    RepaymentFrequency: "Monthly",
    Limit: null,
    Balance: null,
    InterestRate: null,
    RepaymentType: null,
    StartDate: null,
    TotalTermYears: NaN,
    TotalTermMonths: NaN,
    InterestOnlyTermYears: NaN,
    InterestOnlyTermMonths: NaN,
    Lender: null,
    IsRevolvingCredit: null,
  },
} as const;

const LoanForm = ({
  Loans,
  onCancel,
  onSave,
}: {
  Loans: any[];
  onCancel: (e: any) => void;
  onSave: (value: any) => void;
}) => {
  const [showMore, setShowMore] = useState(false);
  const [showLoanTypeWarning, setShowLoanTypeWarning] = useState(false);

  const [LoansList] = useStore(
    useCallback((state) => [Array.from(state.Loans.values())], [])
  );

  const LoanId = useMemo(() => {
    return (
      [...(Loans || []), ...LoansList].reduce((accumulator: number, loan) => {
        return accumulator > loan?.LoanId ? accumulator : loan?.LoanId;
      }, 0) + 1
    );
  }, [Loans, LoansList]);

  const initialValueRef = useRef<Values>({
    LoanId,
    Lender: defaultValues.Lender,
    LoanType: defaultValues.LoanType,
    TopUpAmount: defaultValues.TopUpAmount,
    LendingAction: defaultValues.LendingAction,
    LoanAmount: defaultValues.LoanAmount,
    TotalTermYears: Math.floor(defaultValues.TotalTermMonths / 12),
    TotalTermMonths: defaultValues.TotalTermMonths % 12,
    InterestOnlyTermYears: Math.floor(0 / 12),
    InterestOnlyTermMonths: 0 % 12,
    LiabilityType: defaultValues.LiabilityType,
    Liability: {
      ...defaultValues.Liability,
      InterestRate: undefined,
      TotalTermYears: Math.floor(defaultValues.TotalTermMonths / 12),
      TotalTermMonths: defaultValues.TotalTermMonths % 12,
      InterestOnlyTermYears: Math.floor(0 / 12),
      InterestOnlyTermMonths: 0 % 12,
    },
  });

  const { values, handleChange, hasErrors, resetValues } = useForm<Values>({
    initialValues: initialValueRef.current,
    validate: (values) => {
      const errors: FormErrors<Values> = {};

      if (!values.LoanType) {
        errors.LoanType = "Loan Type can't be empty";
      }

      if (values.LoanType === "New") {
        if (!values.LoanAmount) {
          errors.LoanAmount = "Loan Amount can't be empty or zero";
        }
        if (!values.TotalTermMonths && !values.TotalTermYears) {
          errors.TotalTermMonths = "Loan Term can't be empty or zero";
        }
      } else if (values.LoanType === "Existing") {
        if (!values.LendingAction) {
          errors.LendingAction = "Lending Action can't be empty";
        }

        if (values.LendingAction === "None") {
          if (!values.Liability?.RepaymentAmount) {
            errors.Liability = "Repayments can't be empty or zero";
          }
          if (!values.Liability?.Limit) {
            errors.LoanAmount = "Loan Amount can't be empty or zero";
          }
          if (!values.Liability?.Balance) {
            errors.Liability = "Balance can't be empty or zero";
          }
          if (!values.Liability?.InterestRate) {
            errors.Liability = "Interest rate can't be empty or zero";
          }
          if (
            !values.Liability?.TotalTermMonths &&
            !values.Liability?.TotalTermYears
          ) {
            errors.TotalTermMonths = "Loan Term can't be empty or zero";
          }
          if (values.Liability?.IsRevolvingCredit === null) {
            errors.Liability =
              "Revolving Credit / Redraw Facility can't be empty";
          }
        } else if (values.LendingAction === "Refinance") {
          if (!values.LoanAmount) {
            errors.LoanAmount = "Loan Amount can't be empty or zero";
          }
          if (!values?.TotalTermMonths && !values?.TotalTermYears) {
            errors.TotalTermMonths = "New Loan Term can't be empty or zero";
          }
        } else if (values.LendingAction === "TopUp") {
          if (!values.LoanAmount && !values.Liability?.Limit) {
            errors.LoanAmount = "Original Loan Amount can't be empty or zero";
          }
          if (!values.TopUpAmount) {
            errors.TopUpAmount = "Proposed Increaase can't be empty or zero";
          }
          if (
            !values.Liability?.TotalTermMonths &&
            !values.Liability?.TotalTermYears
          ) {
            errors.TotalTermMonths =
              "Original Loan Term can't be empty or zero";
          }
          if (!values.TotalTermMonths && !values.TotalTermYears) {
            errors.TotalTermMonths = "New Loan Term can't be empty or zero";
          }
          if (values.Lender) {
            if (!values.Liability?.InterestRate) {
              errors.Liability = "Current Interest Rate can't be empty or zero";
            }
            if (!values.Liability?.RepaymentAmount) {
              errors.Liability = " Current Repayments can't be empty or zero";
            }
            if (!values.Liability?.Balance) {
              errors.Liability = "Current Balance Rate can't be empty or zero";
            }
          }
        }
      }

      return errors;
    },
    validateOnMount: true,
  });

  const TotalNewLoanAmount =
    (values.Liability?.Limit || 0) + values.TopUpAmount;

  function handleLoanTypeChange(value: ILoan["LoanType"]) {
    if (values?.LoanType === "Existing" && value === "New") {
      setShowLoanTypeWarning(true);
      return;
    }
    handleChange("LoanType", value);
  }

  function removeLinkedLiability() {
    handleChange("LoanType", "New");
    setShowLoanTypeWarning(false);
  }

  function handleSave() {
    if (!onSave) return;
    onSave?.(values);
  }

  return (
    <>
      <div
        className={classNames(
          "mb-[2px] flex items-center rounded-t-md bg-white px-[15px] py-[10px]"
        )}
      >
        <p className="flex flex-1 items-center gap-4 text-[14px] font-bold text-[#736878]">
          <span>Loan {Loans?.length + 1}</span>
        </p>
        <Button
          className="-mr-2 w-max bg-transparent p-2 text-accent underline"
          size="small"
          onClick={(e) => {
            e.preventDefault();
            onCancel?.(e);
          }}
        >
          <Trash className="w-[10px] desktop:w-[14px]" />
        </Button>
      </div>
      <div className="space-y-3 bg-white p-[15px]">
        <InputContainer className="relative gap-0 border-transparent py-0">
          <Label htmlFor="" className="required">
            Loan Type
          </Label>
          <Alert
            open={showLoanTypeWarning}
            onClose={() => setShowLoanTypeWarning(false)}
            backgroundColor="#1E1642"
            wrapperClassName="min-w-[242px]"
            className="right-0 w-[400px] translate-x-0 translate-y-[10px] text-left font-helvetica text-[11px] text-white shadow-none desktop:text-sm"
            below={
              <InputSwitch
                value={values?.LoanType}
                onChange={handleLoanTypeChange}
              >
                <InputSwitch.InputSwitchOption value="New">
                  NEW
                </InputSwitch.InputSwitchOption>
                <InputSwitch.InputSwitchOption value="Existing">
                  EXISTING
                </InputSwitch.InputSwitchOption>
              </InputSwitch>
            }
          >
            <div className="flex items-center gap-3">
              <span>
                <Info
                  width={48}
                  height={48}
                  fill="currentColor"
                  className="text-[#699AC8]"
                />
              </span>
              <p>
                Changing this loan from from 'Existing' to 'New' will remove the
                linked liability.
              </p>
            </div>
            <div className="mt-3 flex items-center justify-end space-x-[28px]">
              <Button
                type="button"
                size="small"
                className="w-max bg-transparent text-white"
                onClick={() => setShowLoanTypeWarning(false)}
              >
                <span className="font-helvetica text-[11px] leading-[1em] desktop:text-sm">
                  Cancel
                </span>
              </Button>
              <Button
                type="button"
                className="w-[100px] px-4 py-2"
                size="small"
                onClick={removeLinkedLiability}
              >
                <span className="font-helvetica text-[11px] font-bold leading-[1em] desktop:text-sm">
                  OK
                </span>
              </Button>
            </div>
          </Alert>
        </InputContainer>
        {values?.LoanType === "New" && (
          <>
            <InputContainer className="gap-0 border-transparent py-0">
              <Label htmlFor="LoanAmount" className="required">
                Loan Amount
              </Label>
              <InputNumber
                id="LoanAmount"
                buttons={false}
                value={values.LoanAmount}
                onChange={(value) => handleChange(`LoanAmount`, value)}
                withIcon={<Dollar />}
                className="min-w-[242px]"
                autonumeric
              />
            </InputContainer>
            <InputContainer className="gap-0 border-transparent py-0">
              <Label htmlFor="LoanTermYears" className="required">
                Loan Term
              </Label>
              <div className="grid min-w-[242px] grid-cols-2 gap-2">
                <div className="relative">
                  <InputNumber
                    id="LoanTermYears"
                    value={values?.TotalTermYears}
                    onChange={(value) => handleChange(`TotalTermYears`, value)}
                    buttons={false}
                    placeholder="0"
                    pattern="^[0-9]*$"
                  />
                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                    YRS
                  </span>
                </div>
                <div className="relative">
                  <InputNumber
                    id="LoanTermMonths"
                    value={values?.TotalTermMonths ?? undefined}
                    onChange={(value) => handleChange(`TotalTermMonths`, value)}
                    buttons={false}
                    placeholder="0"
                    pattern="^[0-9]*$"
                  />
                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                    MOS
                  </span>
                </div>
              </div>
            </InputContainer>
            <InputContainer className="gap-0 border-transparent py-0">
              <Label htmlFor="InterestOnlyTermYears">Interest-Only Term</Label>
              <div className="grid min-w-[242px] grid-cols-2 gap-2">
                <div className="relative">
                  <InputNumber
                    id="InterestOnlyTermYears"
                    value={values?.InterestOnlyTermYears ?? undefined}
                    onChange={(value) =>
                      handleChange(`InterestOnlyTermYears`, value)
                    }
                    buttons={false}
                    placeholder="0"
                    pattern="^[0-9]*$"
                  />
                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                    YRS
                  </span>
                </div>
                <div className="relative">
                  <InputNumber
                    id="InterestOnlyTermMonths"
                    value={values?.InterestOnlyTermMonths ?? undefined}
                    onChange={(value) =>
                      handleChange(`InterestOnlyTermMonths`, value)
                    }
                    buttons={false}
                    placeholder="0"
                    pattern="^[0-9]*$"
                  />
                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                    MOS
                  </span>
                </div>
              </div>
            </InputContainer>
          </>
        )}
        {values?.LoanType === "Existing" && (
          <>
            <InputContainer className="gap-0 border-transparent py-0">
              <Label htmlFor="LendingAction" className="required">
                Lending Action
              </Label>
              <InputSelect
                className="min-w-[242px]"
                initialValue={values?.LendingAction}
                onChange={(value) => handleChange(`LendingAction`, value)}
              >
                <Option value={"None"}>None</Option>
                <Option value={"Refinance"}>Refinance</Option>
                <Option value={"TopUp"}>Top-Up</Option>
              </InputSelect>
            </InputContainer>
            {values?.LendingAction === null && (
              <>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="LoanAmount" className="required">
                    Loan Amount
                  </Label>
                  <InputNumber
                    id="LoanAmount"
                    buttons={false}
                    value={values?.LoanAmount}
                    onChange={(value) => handleChange(`LoanAmount`, value)}
                    withIcon={<Dollar />}
                    className="min-w-[242px]"
                    autonumeric
                  />
                </InputContainer>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="LoanTermYears" className="required">
                    Loan Term
                  </Label>
                  <div className="grid min-w-[242px] grid-cols-2 gap-2">
                    <div className="relative">
                      <InputNumber
                        id="LoanTermYears"
                        value={values?.TotalTermYears}
                        onChange={(value) =>
                          handleChange(`TotalTermYears`, value)
                        }
                        buttons={false}
                        placeholder="0"
                        pattern="^[0-9]*$"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                        YRS
                      </span>
                    </div>
                    <div className="relative">
                      <InputNumber
                        id="LoanTermMonths"
                        value={values?.TotalTermMonths ?? undefined}
                        onChange={(value) =>
                          handleChange(`TotalTermMonths`, value)
                        }
                        buttons={false}
                        placeholder="0"
                        pattern="^[0-9]*$"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                        MOS
                      </span>
                    </div>
                  </div>
                </InputContainer>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="InterestOnlyTermYears">
                    Interest-Only Term
                  </Label>
                  <div className="grid min-w-[242px] grid-cols-2 gap-2">
                    <div className="relative">
                      <InputNumber
                        id="InterestOnlyTermYears"
                        value={values?.InterestOnlyTermYears}
                        onChange={(value) =>
                          handleChange(`InterestOnlyTermYears`, value)
                        }
                        buttons={false}
                        placeholder="0"
                        pattern="^[0-9]*$"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                        YRS
                      </span>
                    </div>
                    <div className="relative">
                      <InputNumber
                        id="InterestOnlyTermMonths"
                        value={values?.InterestOnlyTermMonths ?? undefined}
                        onChange={(value) =>
                          handleChange(`InterestOnlyTermMonths`, value)
                        }
                        buttons={false}
                        placeholder="0"
                        pattern="^[0-9]*$"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                        MOS
                      </span>
                    </div>
                  </div>
                </InputContainer>
              </>
            )}
            {values?.LendingAction === "None" && (
              <>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="LendingAction" className="required">
                    Repayments
                  </Label>
                  <div className="flex min-w-[242px] items-center rounded-[3px] border border-[#C2B6CB]">
                    <InputNumber
                      id="RepaymentAmount"
                      withIcon={<Dollar />}
                      value={values?.Liability?.RepaymentAmount || undefined}
                      className="rounded-none border-transparent border-r-[#C2B6CB] focus:!outline-none"
                      onChange={(value) => {
                        handleChange(`Liability`, {
                          ...values?.Liability,
                          RepaymentAmount: value,
                        });
                      }}
                      autonumeric
                    />
                    <InputSelect
                      initialValue={values?.Liability?.RepaymentFrequency}
                      onChange={(value) =>
                        handleChange(`Liability`, {
                          ...values?.Liability,
                          RepaymentFrequency: value,
                        })
                      }
                      className="w-[50px]"
                      border="border-0"
                      formatButtonLabel={(value) => value?.charAt(0)}
                    >
                      <Option value={null}></Option>
                      {frequency.map((freq) => (
                        <Option value={freq.value} key={freq.value}>
                          {freq.label}
                        </Option>
                      ))}
                    </InputSelect>
                  </div>
                </InputContainer>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="Limit" className="required">
                    Loan Amount
                  </Label>
                  <InputNumber
                    id="Limit"
                    buttons={false}
                    value={values?.Liability?.Limit ?? NaN}
                    onChange={(value) => {
                      const newValues = {
                        ...values,
                        LoanAmount: value,
                        Liability: {
                          ...values.Liability,
                          Limit: value,
                        },
                      };
                      resetValues(newValues);
                    }}
                    withIcon={<Dollar />}
                    className="min-w-[242px]"
                    autonumeric
                  />
                </InputContainer>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="Balance" className="required">
                    Balance
                  </Label>
                  <InputNumber
                    id="Balance"
                    buttons={false}
                    value={values?.Liability?.Balance ?? NaN}
                    onChange={(value) =>
                      handleChange(`Liability`, {
                        ...values?.Liability,
                        Balance: value,
                      })
                    }
                    withIcon={<Dollar />}
                    className="min-w-[242px]"
                    autonumeric
                  />
                </InputContainer>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="Lender">Lender</Label>
                  <InputSelect
                    initialValue={values?.Liability?.Lender}
                    onChange={(value) =>
                      handleChange(`Liability`, {
                        ...values?.Liability,
                        Lender: value,
                      })
                    }
                    className="min-w-[242px]"
                    searchable={{ placeholder: "Search Lender" }}
                  >
                    <Option value={null}></Option>
                    {LendersData.map((lender) => (
                      <Option value={lender.Code} key={lender.Code}>
                        {lender.Name}
                      </Option>
                    ))}
                  </InputSelect>
                </InputContainer>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="RepaymentType">Repayment Type</Label>
                  <InputSelect
                    initialValue={values?.Liability?.RepaymentType}
                    onChange={(value) =>
                      handleChange(`Liability`, {
                        ...values?.Liability,
                        RepaymentType: value,
                      })
                    }
                    className="min-w-[242px]"
                  >
                    <Option value={null}></Option>
                    {[
                      {
                        value: "PrincipalInterest",
                        label: "Principal & Interest",
                      },
                      {
                        value: "InterestOnly",
                        label: "Interest-Only",
                      },
                    ].map((type) => (
                      <Option value={type.value} key={type.value}>
                        {type.label}
                      </Option>
                    ))}
                  </InputSelect>
                </InputContainer>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="InterestRate" className="required">
                    Interest Rate
                  </Label>
                  <InputNumber
                    id="InterestRate"
                    className="min-w-[242px]"
                    withIcon={<Percent className="opacity-50" />}
                    iconPosition="right"
                    value={values?.Liability?.InterestRate || undefined}
                    onChange={(value) =>
                      handleChange(`Liability`, {
                        ...values?.Liability,
                        InterestRate: value,
                      })
                    }
                  />
                </InputContainer>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="StartDate">Start Date</Label>
                  <InputDate
                    id="StartDate"
                    wrapperClassName="min-w-[242px]"
                    placeholder="DD / MM / YYYY"
                    value={
                      values?.Liability?.StartDate
                        ? parse(
                            values?.Liability.StartDate,
                            "yyyy-MM-dd",
                            new Date()
                          )
                        : undefined
                    }
                    onChange={(date) =>
                      handleChange(`Liability`, {
                        ...values?.Liability,
                        StartDate: format(date, "yyyy-MM-dd"),
                      })
                    }
                  />
                </InputContainer>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="LoanTermYears" className="required">
                    Loan Term
                  </Label>
                  <div className="grid min-w-[242px] grid-cols-2 gap-2">
                    <div className="relative">
                      <InputNumber
                        id="LoanTermYears"
                        value={values?.Liability?.TotalTermYears ?? undefined}
                        onChange={(value) =>
                          handleChange(`Liability`, {
                            ...values?.Liability,
                            TotalTermYears: value,
                          })
                        }
                        buttons={false}
                        placeholder="0"
                        pattern="^[0-9]*$"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                        YRS
                      </span>
                    </div>
                    <div className="relative">
                      <InputNumber
                        id="LoanTermMonths"
                        value={values?.Liability?.TotalTermMonths ?? undefined}
                        onChange={(value) =>
                          handleChange(`Liability`, {
                            ...values?.Liability,
                            TotalTermMonths: value,
                          })
                        }
                        buttons={false}
                        placeholder="0"
                        pattern="^[0-9]*$"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                        MOS
                      </span>
                    </div>
                  </div>
                </InputContainer>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="InterestOnlyTermYears">
                    Interest-Only Term
                  </Label>
                  <div className="grid min-w-[242px] grid-cols-2 gap-2">
                    <div className="relative">
                      <InputNumber
                        id="InterestOnlyTermYears"
                        value={values?.Liability?.InterestOnlyTermYears ?? 0}
                        onChange={(value) =>
                          handleChange(`Liability`, {
                            ...values?.Liability,
                            InterestOnlyTermYears: value,
                          })
                        }
                        buttons={false}
                        placeholder="0"
                        pattern="^[0-9]*$"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                        YRS
                      </span>
                    </div>
                    <div className="relative">
                      <InputNumber
                        id="InterestOnlyTermMonths"
                        value={
                          values?.Liability?.InterestOnlyTermMonths ?? undefined
                        }
                        onChange={(value) =>
                          handleChange(`Liability`, {
                            ...values?.Liability,
                            InterestOnlyTermMonths: value,
                          })
                        }
                        buttons={false}
                        placeholder="0"
                        pattern="^[0-9]*$"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                        MOS
                      </span>
                    </div>
                  </div>
                </InputContainer>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="IsRevolvingCredit" className="required">
                    Revolving Credit / Redraw Facility
                  </Label>
                  <InputSwitch
                    id="IsRevolvingCredit"
                    value={values?.Liability?.IsRevolvingCredit}
                    onChange={(value) =>
                      handleChange(`Liability`, {
                        ...values?.Liability,
                        IsRevolvingCredit: value,
                      })
                    }
                    className="min-w-[242px]"
                  >
                    <InputSwitch.InputSwitchOption value={true}>
                      Yes
                    </InputSwitch.InputSwitchOption>
                    <InputSwitch.InputSwitchOption value={false}>
                      No
                    </InputSwitch.InputSwitchOption>
                  </InputSwitch>
                </InputContainer>
              </>
            )}
            {values?.LendingAction === "Refinance" && (
              <>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="LoanAmount" className="required">
                    Loan Amount
                  </Label>
                  <InputNumber
                    id="LoanAmount"
                    buttons={false}
                    value={
                      !isNaN(values?.LoanAmount)
                        ? values?.LoanAmount
                        : values?.Liability?.Limit || undefined
                    }
                    onChange={(value) => {
                      const newValue = {
                        ...values,
                        LoanAmount: value,
                        Liability: {
                          ...values?.Liability,
                          Limit: value,
                        },
                      };
                      resetValues(newValue);
                    }}
                    withIcon={<Dollar />}
                    className="min-w-[242px]"
                    autonumeric
                  />
                </InputContainer>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="OriginalLoanTermYears">
                    Original Loan Term
                  </Label>
                  <div className="grid min-w-[242px] grid-cols-2 gap-2">
                    <div className="relative">
                      <InputNumber
                        id="OriginalLoanTermYears"
                        value={values?.Liability?.TotalTermYears ?? undefined}
                        onChange={(value) =>
                          resetValues({
                            ...values,
                            Liability: {
                              ...values?.Liability,
                              TotalTermYears: value,
                            },
                            TotalTermYears: value,
                          })
                        }
                        buttons={false}
                        placeholder="0"
                        pattern="^[0-9]*$"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                        YRS
                      </span>
                    </div>
                    <div className="relative">
                      <InputNumber
                        id="OriginalLoanTermMonths"
                        value={values?.Liability?.TotalTermMonths ?? undefined}
                        onChange={(value) =>
                          resetValues({
                            ...values,
                            Liability: {
                              ...values?.Liability,
                              TotalTermMonths: value,
                            },
                            TotalTermMonths: value,
                          })
                        }
                        buttons={false}
                        placeholder="0"
                        pattern="^[0-9]*$"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                        MOS
                      </span>
                    </div>
                  </div>
                </InputContainer>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="LoanTermYears" className="required">
                    New Loan Term
                  </Label>
                  <div className="grid min-w-[242px] grid-cols-2 gap-2">
                    <div className="relative">
                      <InputNumber
                        id="LoanTermYears"
                        value={values?.TotalTermYears}
                        onChange={(value) =>
                          handleChange(`TotalTermYears`, value)
                        }
                        buttons={false}
                        placeholder="0"
                        pattern="^[0-9]*$"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                        YRS
                      </span>
                    </div>
                    <div className="relative">
                      <InputNumber
                        id="LoanTermMonths"
                        value={values?.TotalTermMonths ?? undefined}
                        onChange={(value) =>
                          handleChange(`TotalTermMonths`, value)
                        }
                        buttons={false}
                        placeholder="0"
                        pattern="^[0-9]*$"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                        MOS
                      </span>
                    </div>
                  </div>
                </InputContainer>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="InterestOnlyTermYears">New IO Term</Label>
                  <div className="grid min-w-[242px] grid-cols-2 gap-2">
                    <div className="relative">
                      <InputNumber
                        id="InterestOnlyTermYears"
                        value={values?.InterestOnlyTermYears ?? 0}
                        onChange={(value) =>
                          handleChange(`InterestOnlyTermYears`, value)
                        }
                        buttons={false}
                        placeholder="0"
                        pattern="^[0-9]*$"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                        YRS
                      </span>
                    </div>
                    <div className="relative">
                      <InputNumber
                        id="InterestOnlyTermMonths"
                        value={values?.InterestOnlyTermMonths ?? undefined}
                        onChange={(value) =>
                          handleChange(`InterestOnlyTermMonths`, value)
                        }
                        buttons={false}
                        placeholder="0"
                        pattern="^[0-9]*$"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                        MOS
                      </span>
                    </div>
                  </div>
                </InputContainer>
                <InputContainer className="justify-end gap-0 border-transparent py-0">
                  <Label
                    htmlFor={"ShowMore" + (Loans.length + 1)}
                    className="w-[242px] font-bold text-accent"
                  >
                    {showMore ? "Show Less" : "Show More"}...
                  </Label>
                  <input
                    checked={showMore}
                    onChange={() => setShowMore(!showMore)}
                    type="checkbox"
                    className="hidden"
                    id={"ShowMore" + (Loans.length + 1)}
                    name={"ShowMore" + (Loans.length + 1)}
                  />
                </InputContainer>
                <AnimatePresence>
                  {showMore && (
                    <motion.div
                      initial={{
                        height: 0,
                        opacity: 0,
                      }}
                      animate={{
                        height: "auto",
                        opacity: 1,
                      }}
                      exit={{ height: 0, opacity: 0, marginTop: 0 }}
                      transition={{ duration: 0.2, ease: "linear" }}
                      className="!mt-0 space-y-2"
                    >
                      <SectionLabel className="!text-[13px] desktop:!text-sm">
                        Existing Loan Details
                      </SectionLabel>
                      <InputContainer className="gap-0 border-transparent py-0">
                        <Label htmlFor="Lender">Lender</Label>
                        <InputSelect
                          initialValue={values?.Lender}
                          onChange={(value) => {
                            const newValue = {
                              ...values,
                              Lender: value,
                              Liability: {
                                ...values?.Liability,
                                Lender: value,
                              },
                            };
                            resetValues(newValue);
                          }}
                          className="min-w-[242px]"
                          searchable={{ placeholder: "Search Lender" }}
                        >
                          <Option value={null}></Option>
                          {LendersData.map((lender) => (
                            <Option value={lender.Code} key={lender.Code}>
                              {lender.Name}
                            </Option>
                          ))}
                        </InputSelect>
                      </InputContainer>
                      <InputContainer className="gap-0 border-transparent py-0">
                        <Label htmlFor="LendingAction">Repayments</Label>
                        <div className="flex min-w-[242px] items-center rounded-[3px] border border-[#C2B6CB]">
                          <InputNumber
                            id="RepaymentAmount"
                            withIcon={<Dollar />}
                            value={
                              values?.Liability?.RepaymentAmount || undefined
                            }
                            className="rounded-none border-transparent border-r-[#C2B6CB] focus:!outline-none"
                            onChange={(value) => {
                              handleChange(`Liability`, {
                                ...values?.Liability,
                                RepaymentAmount: value,
                              });
                            }}
                            autonumeric
                          />
                          <InputSelect
                            initialValue={values?.Liability?.RepaymentFrequency}
                            onChange={(value) =>
                              handleChange(`Liability`, {
                                ...values?.Liability,
                                RepaymentFrequency: value,
                              })
                            }
                            className="w-[50px]"
                            border="border-0"
                            formatButtonLabel={(value) => value?.charAt(0)}
                          >
                            <Option value={null}></Option>
                            {frequency.map((freq) => (
                              <Option value={freq.value} key={freq.value}>
                                {freq.label}
                              </Option>
                            ))}
                          </InputSelect>
                        </div>
                      </InputContainer>
                      <InputContainer className="gap-0 border-transparent py-0">
                        <Label htmlFor="Balance">Balance</Label>
                        <InputNumber
                          id="Balance"
                          buttons={false}
                          value={values?.Liability?.Balance ?? NaN}
                          onChange={(value) =>
                            handleChange(`Liability`, {
                              ...values?.Liability,
                              Balance: value,
                            })
                          }
                          withIcon={<Dollar />}
                          className="min-w-[242px]"
                          autonumeric
                        />
                      </InputContainer>
                      <InputContainer className="gap-0 border-transparent py-0">
                        <Label htmlFor="InterestRate">Interest Rate</Label>
                        <InputNumber
                          id="InterestRate"
                          className="min-w-[242px]"
                          withIcon={<Percent className="opacity-50" />}
                          iconPosition="right"
                          value={values?.Liability?.InterestRate || undefined}
                          onChange={(value) =>
                            handleChange(`Liability`, {
                              ...values?.Liability,
                              InterestRate: value,
                            })
                          }
                        />
                      </InputContainer>
                      <InputContainer className="gap-0 border-transparent py-0">
                        <Label htmlFor="RepaymentType">Repayment Type</Label>
                        <InputSelect
                          initialValue={values?.Liability?.RepaymentType}
                          onChange={(value) =>
                            handleChange(`Liability`, {
                              ...values?.Liability,
                              RepaymentType: value,
                            })
                          }
                          className="min-w-[242px]"
                        >
                          <Option value={null}></Option>
                          {[
                            {
                              value: "PrincipalInterest",
                              label: "Principal & Interest",
                            },
                            {
                              value: "InterestOnly",
                              label: "Interest-Only",
                            },
                          ].map((type) => (
                            <Option value={type.value} key={type.value}>
                              {type.label}
                            </Option>
                          ))}
                        </InputSelect>
                      </InputContainer>
                      <InputContainer className="gap-0 border-transparent py-0">
                        <Label htmlFor="StartDate">Start Date</Label>
                        <InputDate
                          id="StartDate"
                          wrapperClassName="min-w-[242px]"
                          placeholder="DD / MM / YYYY"
                          value={
                            values?.Liability?.StartDate
                              ? parse(
                                  values?.Liability.StartDate,
                                  "yyyy-MM-dd",
                                  new Date()
                                )
                              : undefined
                          }
                          onChange={(date) =>
                            handleChange(`Liability`, {
                              ...values?.Liability,
                              StartDate: format(date, "yyyy-MM-dd"),
                            })
                          }
                        />
                      </InputContainer>
                      <InputContainer className="gap-0 border-transparent py-0">
                        <Label htmlFor="InterestOnlyTermYears">
                          Interest-Only Term
                        </Label>
                        <div className="grid min-w-[242px] grid-cols-2 gap-2">
                          <div className="relative">
                            <InputNumber
                              id="InterestOnlyTermYears"
                              value={
                                values?.Liability?.InterestOnlyTermYears ?? 0
                              }
                              onChange={(value) =>
                                handleChange(`Liability`, {
                                  ...values?.Liability,
                                  InterestOnlyTermYears: value,
                                })
                              }
                              buttons={false}
                              placeholder="0"
                              pattern="^[0-9]*$"
                            />
                            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                              YRS
                            </span>
                          </div>
                          <div className="relative">
                            <InputNumber
                              id="InterestOnlyTermMonths"
                              value={
                                values?.Liability?.InterestOnlyTermMonths ??
                                undefined
                              }
                              onChange={(value) =>
                                handleChange(`Liability`, {
                                  ...values?.Liability,
                                  InterestOnlyTermMonths: value,
                                })
                              }
                              buttons={false}
                              placeholder="0"
                              pattern="^[0-9]*$"
                            />
                            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                              MOS
                            </span>
                          </div>
                        </div>
                      </InputContainer>
                      <InputContainer className="gap-0 border-transparent py-0">
                        <Label htmlFor="IsRevolvingCredit">
                          Revolving Credit / Redraw Facility
                        </Label>
                        <InputSwitch
                          id="IsRevolvingCredit"
                          value={values?.Liability?.IsRevolvingCredit}
                          onChange={(value) =>
                            handleChange(`Liability`, {
                              ...values?.Liability,
                              IsRevolvingCredit: value,
                            })
                          }
                          className="min-w-[242px]"
                        >
                          <InputSwitch.InputSwitchOption value={true}>
                            Yes
                          </InputSwitch.InputSwitchOption>
                          <InputSwitch.InputSwitchOption value={false}>
                            No
                          </InputSwitch.InputSwitchOption>
                        </InputSwitch>
                      </InputContainer>
                    </motion.div>
                  )}
                </AnimatePresence>
              </>
            )}
            {values?.LendingAction === "TopUp" && (
              <>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="LoanAmount" className="required">
                    Original Loan Amount
                  </Label>
                  <InputNumber
                    id="LoanAmount"
                    buttons={false}
                    value={
                      !isNaN(values?.LoanAmount)
                        ? values?.LoanAmount
                        : values?.Liability?.Limit || undefined
                    }
                    onChange={(value) => {
                      const newValues = {
                        ...values,
                        LoanAmount: value,
                        Liability: { ...values.Liability, Limit: value },
                      };
                      resetValues(newValues);
                    }}
                    withIcon={<Dollar />}
                    className="min-w-[242px]"
                    autonumeric
                  />
                </InputContainer>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="TopUpAmount" className="required">
                    Proposed Increase
                  </Label>
                  <InputNumber
                    id="TopUpAmount"
                    buttons={false}
                    value={values?.TopUpAmount}
                    onChange={(value) => {
                      handleChange(`TopUpAmount`, value);
                    }}
                    withIcon={<Dollar />}
                    className="min-w-[242px]"
                    autonumeric
                  />
                </InputContainer>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="NewLoanTotal">New Loan Total</Label>
                  <InputNumber
                    key={TotalNewLoanAmount}
                    id="NewLoanTotal"
                    buttons={false}
                    value={TotalNewLoanAmount}
                    withIcon={<Dollar />}
                    className="min-w-[242px]"
                    disabled
                    autonumeric
                  />
                </InputContainer>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="OriginalLoanTermYears" className="required">
                    Original Loan Term
                  </Label>
                  <div className="grid min-w-[242px] grid-cols-2 gap-2">
                    <div className="relative">
                      <InputNumber
                        id="OriginalLoanTermYears"
                        value={values?.Liability?.TotalTermYears ?? undefined}
                        onChange={(value) =>
                          resetValues({
                            ...values,
                            Liability: {
                              ...values?.Liability,
                              TotalTermYears: value,
                            },
                            TotalTermYears: value,
                          })
                        }
                        buttons={false}
                        placeholder="0"
                        pattern="^[0-9]*$"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                        YRS
                      </span>
                    </div>
                    <div className="relative">
                      <InputNumber
                        id="OriginalLoanTermMonths"
                        value={values?.Liability?.TotalTermMonths ?? undefined}
                        onChange={(value) =>
                          resetValues({
                            ...values,
                            Liability: {
                              ...values?.Liability,
                              TotalTermMonths: value,
                            },
                            TotalTermMonths: value,
                          })
                        }
                        buttons={false}
                        placeholder="0"
                        pattern="^[0-9]*$"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                        MOS
                      </span>
                    </div>
                  </div>
                </InputContainer>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="LoanTermYears" className="required">
                    New Loan Term
                  </Label>
                  <div className="grid min-w-[242px] grid-cols-2 gap-2">
                    <div className="relative">
                      <InputNumber
                        id="LoanTermYears"
                        value={values?.TotalTermYears}
                        onChange={(value) =>
                          handleChange(`TotalTermYears`, value)
                        }
                        buttons={false}
                        placeholder="0"
                        pattern="^[0-9]*$"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                        YRS
                      </span>
                    </div>
                    <div className="relative">
                      <InputNumber
                        id="LoanTermMonths"
                        value={values?.TotalTermMonths ?? undefined}
                        onChange={(value) =>
                          handleChange(`TotalTermMonths`, value)
                        }
                        buttons={false}
                        placeholder="0"
                        pattern="^[0-9]*$"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                        MOS
                      </span>
                    </div>
                  </div>
                </InputContainer>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="InterestOnlyTermYears">New IO Term</Label>
                  <div className="grid min-w-[242px] grid-cols-2 gap-2">
                    <div className="relative">
                      <InputNumber
                        id="InterestOnlyTermYears"
                        value={values?.InterestOnlyTermYears ?? 0}
                        onChange={(value) =>
                          handleChange(`InterestOnlyTermYears`, value)
                        }
                        buttons={false}
                        placeholder="0"
                        pattern="^[0-9]*$"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                        YRS
                      </span>
                    </div>
                    <div className="relative">
                      <InputNumber
                        id="InterestOnlyTermMonths"
                        value={values?.InterestOnlyTermMonths ?? undefined}
                        onChange={(value) =>
                          handleChange(`InterestOnlyTermMonths`, value)
                        }
                        buttons={false}
                        placeholder="0"
                        pattern="^[0-9]*$"
                      />
                      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                        MOS
                      </span>
                    </div>
                  </div>
                </InputContainer>
                <InputContainer className="gap-0 border-transparent py-0">
                  <Label htmlFor="Lender">Current Lender</Label>
                  <InputSelect
                    initialValue={values?.Lender ?? null}
                    onChange={(value) => {
                      const newValue = {
                        ...values,
                        Lender: value,
                        Liability: {
                          ...values?.Liability,
                          Lender: value,
                        },
                      };
                      resetValues(newValue);
                    }}
                    className="min-w-[242px]"
                    searchable={{ placeholder: "Search Lender" }}
                  >
                    <Option value={null}></Option>
                    {LendersData.map((lender) => (
                      <Option value={lender.Code} key={lender.Code}>
                        {lender.Name}
                      </Option>
                    ))}
                  </InputSelect>
                </InputContainer>
                {values?.Lender === null && (
                  <>
                    <InputContainer className="justify-end gap-0 border-transparent py-0">
                      <Label
                        htmlFor={"ShowMore" + (Loans.length + 1)}
                        className="w-[242px] font-bold text-accent"
                      >
                        {showMore ? "Show Less" : "Show More"}...
                      </Label>
                      <input
                        checked={showMore}
                        onChange={() => setShowMore(!showMore)}
                        type="checkbox"
                        className="hidden"
                        id={"ShowMore" + (Loans.length + 1)}
                        name={"ShowMore" + (Loans.length + 1)}
                      />
                    </InputContainer>
                    <AnimatePresence>
                      {showMore && (
                        <motion.div
                          initial={{
                            height: 0,
                            opacity: 0,
                          }}
                          animate={{
                            height: "auto",
                            opacity: 1,
                          }}
                          exit={{ height: 0, opacity: 0, marginTop: 0 }}
                          transition={{ duration: 0.2, ease: "linear" }}
                          className="!mt-0 space-y-2"
                        >
                          <SectionLabel className="!text-[13px] desktop:!text-sm">
                            Existing Loan Details
                          </SectionLabel>
                          <InputContainer className="gap-0 border-transparent py-0">
                            <Label htmlFor="LendingAction">Repayments</Label>
                            <div className="flex min-w-[242px] items-center rounded-[3px] border border-[#C2B6CB]">
                              <InputNumber
                                id="RepaymentAmount"
                                withIcon={<Dollar />}
                                value={
                                  values?.Liability?.RepaymentAmount ||
                                  undefined
                                }
                                className="rounded-none border-transparent border-r-[#C2B6CB] focus:!outline-none"
                                onChange={(value) => {
                                  handleChange(`Liability`, {
                                    ...values?.Liability,
                                    RepaymentAmount: value,
                                  });
                                }}
                                autonumeric
                              />
                              <InputSelect
                                initialValue={
                                  values?.Liability?.RepaymentFrequency
                                }
                                onChange={(value) =>
                                  handleChange(`Liability`, {
                                    ...values?.Liability,
                                    RepaymentFrequency: value,
                                  })
                                }
                                className="w-[50px]"
                                border="border-0"
                                formatButtonLabel={(value) => value?.charAt(0)}
                              >
                                <Option value={null}></Option>
                                {frequency.map((freq) => (
                                  <Option value={freq.value} key={freq.value}>
                                    {freq.label}
                                  </Option>
                                ))}
                              </InputSelect>
                            </div>
                          </InputContainer>
                          <InputContainer className="gap-0 border-transparent py-0">
                            <Label htmlFor="Balance">Balance</Label>
                            <InputNumber
                              id="Balance"
                              buttons={false}
                              value={values?.Liability?.Balance ?? NaN}
                              onChange={(value) =>
                                handleChange(`Liability`, {
                                  ...values?.Liability,
                                  Balance: value,
                                })
                              }
                              withIcon={<Dollar />}
                              className="min-w-[242px]"
                              autonumeric
                            />
                          </InputContainer>
                          <InputContainer className="gap-0 border-transparent py-0">
                            <Label htmlFor="InterestRate">Interest Rate</Label>
                            <InputNumber
                              id="InterestRate"
                              className="min-w-[242px]"
                              withIcon={<Percent className="opacity-50" />}
                              iconPosition="right"
                              value={values?.Liability?.InterestRate || 0}
                              onChange={(value) =>
                                handleChange(`Liability`, {
                                  ...values?.Liability,
                                  InterestRate: value,
                                })
                              }
                            />
                          </InputContainer>
                          <InputContainer className="gap-0 border-transparent py-0">
                            <Label htmlFor="RepaymentType">
                              Repayment Type
                            </Label>
                            <InputSelect
                              initialValue={values?.Liability?.RepaymentType}
                              onChange={(value) =>
                                handleChange(`Liability`, {
                                  ...values?.Liability,
                                  RepaymentType: value,
                                })
                              }
                              className="min-w-[242px]"
                            >
                              <Option value={null}></Option>
                              {[
                                {
                                  value: "PrincipalInterest",
                                  label: "Principal & Interest",
                                },
                                {
                                  value: "InterestOnly",
                                  label: "Interest-Only",
                                },
                              ].map((type) => (
                                <Option value={type.value} key={type.value}>
                                  {type.label}
                                </Option>
                              ))}
                            </InputSelect>
                          </InputContainer>
                          <InputContainer className="gap-0 border-transparent py-0">
                            <Label htmlFor="StartDate">Start Date</Label>
                            <InputDate
                              id="StartDate"
                              wrapperClassName="min-w-[242px]"
                              placeholder="DD / MM / YYYY"
                              value={
                                values?.Liability?.StartDate
                                  ? parse(
                                      values?.Liability.StartDate,
                                      "yyyy-MM-dd",
                                      new Date()
                                    )
                                  : undefined
                              }
                              onChange={(date) =>
                                handleChange(`Liability`, {
                                  ...values?.Liability,
                                  StartDate: format(date, "yyyy-MM-dd"),
                                })
                              }
                            />
                          </InputContainer>
                          <InputContainer className="gap-0 border-transparent py-0">
                            <Label htmlFor="InterestOnlyTermYears">
                              Interest-Only Term
                            </Label>
                            <div className="grid min-w-[242px] grid-cols-2 gap-2">
                              <div className="relative">
                                <InputNumber
                                  id="InterestOnlyTermYears"
                                  value={
                                    values?.Liability?.InterestOnlyTermYears ??
                                    0
                                  }
                                  onChange={(value) =>
                                    handleChange(`Liability`, {
                                      ...values?.Liability,
                                      InterestOnlyTermYears: value,
                                    })
                                  }
                                  buttons={false}
                                  placeholder="0"
                                  pattern="^[0-9]*$"
                                />
                                <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                                  YRS
                                </span>
                              </div>
                              <div className="relative">
                                <InputNumber
                                  id="InterestOnlyTermMonths"
                                  value={
                                    values?.Liability?.InterestOnlyTermMonths ??
                                    undefined
                                  }
                                  onChange={(value) =>
                                    handleChange(`Liability`, {
                                      ...values?.Liability,
                                      InterestOnlyTermMonths: value,
                                    })
                                  }
                                  buttons={false}
                                  placeholder="0"
                                  pattern="^[0-9]*$"
                                />
                                <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                                  MOS
                                </span>
                              </div>
                            </div>
                          </InputContainer>
                          <InputContainer className="gap-0 border-transparent py-0">
                            <Label htmlFor="IsRevolvingCredit">
                              Revolving Credit / Redraw Facility
                            </Label>
                            <InputSwitch
                              id="IsRevolvingCredit"
                              value={values?.Liability?.IsRevolvingCredit}
                              onChange={(value) =>
                                handleChange(`Liability`, {
                                  ...values?.Liability,
                                  IsRevolvingCredit: value,
                                })
                              }
                              className="min-w-[242px]"
                            >
                              <InputSwitch.InputSwitchOption value={true}>
                                Yes
                              </InputSwitch.InputSwitchOption>
                              <InputSwitch.InputSwitchOption value={false}>
                                No
                              </InputSwitch.InputSwitchOption>
                            </InputSwitch>
                          </InputContainer>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}
                {values?.Lender !== null && (
                  <>
                    <InputContainer className="gap-0 border-transparent py-0">
                      <Label htmlFor="InterestRate" className="required">
                        Current Interest Rate
                      </Label>
                      <InputNumber
                        id="InterestRate"
                        className="min-w-[242px]"
                        withIcon={<Percent className="opacity-50" />}
                        iconPosition="right"
                        value={values?.Liability?.InterestRate || undefined}
                        onChange={(value) =>
                          handleChange(`Liability`, {
                            ...values?.Liability,
                            InterestRate: value,
                          })
                        }
                      />
                    </InputContainer>
                    <InputContainer className="gap-0 border-transparent py-0">
                      <Label htmlFor="LendingAction" className="required">
                        Current Repayments
                      </Label>
                      <div className="flex min-w-[242px] items-center rounded-[3px] border border-[#C2B6CB]">
                        <InputNumber
                          id="RepaymentAmount"
                          withIcon={<Dollar />}
                          value={
                            values?.Liability?.RepaymentAmount || undefined
                          }
                          className="rounded-none border-transparent border-r-[#C2B6CB] focus:!outline-none"
                          onChange={(value) => {
                            handleChange(`Liability`, {
                              ...values?.Liability,
                              RepaymentAmount: value,
                            });
                          }}
                          autonumeric
                        />
                        <InputSelect
                          initialValue={values?.Liability?.RepaymentFrequency}
                          onChange={(value) =>
                            handleChange(`Liability`, {
                              ...values?.Liability,
                              RepaymentFrequency: value,
                            })
                          }
                          className="w-[50px]"
                          border="border-0"
                          formatButtonLabel={(value) => value?.charAt(0)}
                        >
                          <Option value={null}></Option>
                          {frequency.map((freq) => (
                            <Option value={freq.value} key={freq.value}>
                              {freq.label}
                            </Option>
                          ))}
                        </InputSelect>
                      </div>
                    </InputContainer>
                    <InputContainer className="gap-0 border-transparent py-0">
                      <Label htmlFor="Balance" className="required">
                        Current Balance
                      </Label>
                      <InputNumber
                        id="Balance"
                        buttons={false}
                        value={values?.Liability?.Balance ?? NaN}
                        onChange={(value) =>
                          handleChange(`Liability`, {
                            ...values?.Liability,
                            Balance: value,
                          })
                        }
                        withIcon={<Dollar />}
                        className="min-w-[242px]"
                        autonumeric
                      />
                    </InputContainer>
                    <InputContainer className="justify-end gap-0 border-transparent py-0">
                      <Label
                        htmlFor={"ShowMore" + (Loans.length + 1)}
                        className="w-[242px] font-bold text-accent"
                      >
                        {showMore ? "Show Less" : "Show More"}...
                      </Label>
                      <input
                        checked={showMore}
                        onChange={() => setShowMore(!showMore)}
                        type="checkbox"
                        className="hidden"
                        id={"ShowMore" + (Loans.length + 1)}
                        name={"ShowMore" + (Loans.length + 1)}
                      />
                    </InputContainer>
                    <AnimatePresence>
                      {showMore && (
                        <motion.div
                          initial={{
                            height: 0,
                            opacity: 0,
                          }}
                          animate={{
                            height: "auto",
                            opacity: 1,
                          }}
                          exit={{ height: 0, opacity: 0, marginTop: 0 }}
                          transition={{ duration: 0.2, ease: "linear" }}
                          className="!mt-0 space-y-2"
                        >
                          <SectionLabel className="!text-[13px] desktop:!text-sm">
                            Existing Loan Details
                          </SectionLabel>
                          <InputContainer className="gap-0 border-transparent py-0">
                            <Label htmlFor="RepaymentType">
                              Repayment Type
                            </Label>
                            <InputSelect
                              initialValue={values?.Liability?.RepaymentType}
                              onChange={(value) =>
                                handleChange(`Liability`, {
                                  ...values?.Liability,
                                  RepaymentType: value,
                                })
                              }
                              className="min-w-[242px]"
                            >
                              <Option value={null}></Option>
                              {[
                                {
                                  value: "PrincipalInterest",
                                  label: "Principal & Interest",
                                },
                                {
                                  value: "InterestOnly",
                                  label: "Interest-Only",
                                },
                              ].map((type) => (
                                <Option value={type.value} key={type.value}>
                                  {type.label}
                                </Option>
                              ))}
                            </InputSelect>
                          </InputContainer>
                          <InputContainer className="gap-0 border-transparent py-0">
                            <Label htmlFor="StartDate">Start Date</Label>
                            <InputDate
                              id="StartDate"
                              wrapperClassName="min-w-[242px]"
                              placeholder="DD / MM / YYYY"
                              value={
                                values?.Liability?.StartDate
                                  ? parse(
                                      values?.Liability.StartDate,
                                      "yyyy-MM-dd",
                                      new Date()
                                    )
                                  : undefined
                              }
                              onChange={(date) =>
                                handleChange(`Liability`, {
                                  ...values?.Liability,
                                  StartDate: format(date, "yyyy-MM-dd"),
                                })
                              }
                            />
                          </InputContainer>
                          <InputContainer className="gap-0 border-transparent py-0">
                            <Label htmlFor="InterestOnlyTermYears">
                              Interest-Only Term
                            </Label>
                            <div className="grid min-w-[242px] grid-cols-2 gap-2">
                              <div className="relative">
                                <InputNumber
                                  id="InterestOnlyTermYears"
                                  value={
                                    values?.Liability?.InterestOnlyTermYears ??
                                    0
                                  }
                                  onChange={(value) =>
                                    handleChange(`Liability`, {
                                      ...values?.Liability,
                                      InterestOnlyTermYears: value,
                                    })
                                  }
                                  buttons={false}
                                  placeholder="0"
                                  pattern="^[0-9]*$"
                                />
                                <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                                  YRS
                                </span>
                              </div>
                              <div className="relative">
                                <InputNumber
                                  id="InterestOnlyTermMonths"
                                  value={
                                    values?.Liability?.InterestOnlyTermMonths ??
                                    undefined
                                  }
                                  onChange={(value) =>
                                    handleChange(`Liability`, {
                                      ...values?.Liability,
                                      InterestOnlyTermMonths: value,
                                    })
                                  }
                                  buttons={false}
                                  placeholder="0"
                                  pattern="^[0-9]*$"
                                />
                                <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                                  MOS
                                </span>
                              </div>
                            </div>
                          </InputContainer>
                          <InputContainer className="gap-0 border-transparent py-0">
                            <Label htmlFor="IsRevolvingCredit">
                              Revolving Credit / Redraw Facility
                            </Label>
                            <InputSwitch
                              id="IsRevolvingCredit"
                              value={values?.Liability?.IsRevolvingCredit}
                              onChange={(value) =>
                                handleChange(`Liability`, {
                                  ...values?.Liability,
                                  IsRevolvingCredit: value,
                                })
                              }
                              className="min-w-[242px]"
                            >
                              <InputSwitch.InputSwitchOption value={true}>
                                Yes
                              </InputSwitch.InputSwitchOption>
                              <InputSwitch.InputSwitchOption value={false}>
                                No
                              </InputSwitch.InputSwitchOption>
                            </InputSwitch>
                          </InputContainer>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
      <footer className="mt-[2px] flex items-center space-x-2 rounded-b-md bg-white p-[15px]">
        <Button
          type="button"
          size="medium"
          className="max-w-[83px] border border-transparent bg-transparent text-[#7B5C8D] hover:border-[#7B5C8D] desktop:text-sm"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          size="medium"
          className="max-w-[83px] disabled:cursor-not-allowed disabled:bg-[#C2B6CB] desktop:text-sm"
          disabled={hasErrors}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleSave();
          }}
        >
          Save
        </Button>
      </footer>
    </>
  );
};

const AddLoanForm = ({
  Loans,
  isEditing,
  onSave,
  onAdding,
}: {
  Loans: any[];
  isEditing: boolean;
  onSave: (values: any) => void;
  onAdding: (status: boolean) => void;
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const hasData = (Loans || []).length > 0;

  useEffect(() => {
    if ((Loans || []).length > 0) return;
    setIsAdding((Loans || []).length === 0);
  }, [Loans]);

  const openForm = useCallback(
    (e) => {
      e.preventDefault();
      if (isEditing) return;
      setIsAdding(true);
      onAdding?.(true);
    },
    [isEditing, onAdding]
  );
  const closeForm = useCallback(
    (e) => {
      e.preventDefault();
      setIsAdding(false);
      onAdding?.(false);
    },
    [onAdding]
  );

  return (
    <AnimatePresence>
      {isAdding ? (
        <motion.div
          key="form"
          initial={{
            height: !hasData ? "auto" : 0,
            marginTop: 0,
            overflowY: "hidden",
          }}
          exit={{
            height: 0,
            marginTop: 0,
            overflowY: "hidden",
          }}
          animate={{
            height: "auto",
            marginTop: 0,
            overflowY: "visible",
          }}
          transition={{
            ease: "easeInOut",
            duration: 0.2,
          }}
        >
          <LoanForm
            Loans={Loans}
            onCancel={closeForm}
            onSave={(values) => {
              onSave(values);
              setIsAdding(false);
              onAdding?.(false);
            }}
          />
        </motion.div>
      ) : (
        <motion.div
          key="button"
          initial={{
            height: !hasData ? "auto" : 0,
          }}
          exit={{
            height: 0,
          }}
          animate={{
            height: "auto",
          }}
          transition={{
            ease: "easeInOut",
            duration: 0.2,
          }}
          className="overflow-y-hidden"
        >
          <Button
            className={classNames(
              "w-max bg-transparent font-bold text-accent desktop:text-sm",
              isEditing ? "cursor-not-allowed opacity-50" : "cursor-pointer"
            )}
            size="small"
            withIcon={
              <span className="rounded-full bg-accent p-[3px]">
                <Plus fill="white" />
              </span>
            }
            onClick={openForm}
          >
            Add Loan
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddLoanForm;
